import * as React from 'react'
import {NAVY, WHITE, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'
import {FONT} from '../../../../styles/foundation/typography'
import {dims as fmtDims} from '../format'

const W = 220
const H = 140
const BR = 16 // corner registration mark arm length

const corner = (x, y, sx, sy) => `M ${x + sx * BR} ${y} L ${x} ${y} L ${x} ${y + sy * BR}`

/**
 * The scanner plate. A DATA PLACEHOLDER, drawn as the readout rather than as
 * a picture, because there are no real DWS photographs in this dataset and
 * nothing here is allowed to pretend one exists. It prints the readings the
 * scanner actually took and stamps "No photograph on file" on the face.
 *
 * Used for every courier record, and for any merchant record whose file is
 * not present (every seeded upload — only files picked in this session carry
 * a real blob url). See recordImageUrl()/recordVideoUrl() in DwsThumb.js for
 * that decision.
 *
 * Colours come from the foundation tokens; the dark face is NAVY rather than
 * a bespoke media background, so the plate sits inside the app's palette
 * instead of importing the mockup's --pz-media-bg.
 */
export default function DwsPlate({record}) {
  const kg = Number(record.read || 0).toFixed(2)
  const label = `Scanner readings for AWB ${record.awb}. `
    + `Weight ${kg} kilograms, ${fmtDims(record.dims)}. No photograph on file.`

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width='100%'
      preserveAspectRatio='xMidYMid meet'
      role='img'
      aria-label={label}
      style={{display: 'block', aspectRatio: '220 / 140'}}
    >
      <rect x='0' y='0' width={W} height={H} rx='8' fill={NAVY} />

      <g stroke={PRIMARY_BLUE} strokeWidth='1.4' fill='none' opacity='0.85'>
        <path d={corner(12, 12, 1, 1)} />
        <path d={corner(W - 12, 12, -1, 1)} />
        <path d={corner(12, H - 12, 1, -1)} />
        <path d={corner(W - 12, H - 12, -1, -1)} />
      </g>

      <text x='18' y='30' fill={withOpacity(WHITE, 0.82)} style={{fontFamily: FONT, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em'}}>
        DWS READ
      </text>
      <text x={W - 18} y='30' textAnchor='end' fill={withOpacity(WHITE, 0.82)} style={{fontFamily: 'monospace', fontSize: 10}}>
        {record.awb}
      </text>
      <line x1='18' y1='40' x2={W - 18} y2='40' stroke={WHITE} strokeWidth='1' opacity='0.28' />

      <text x='18' y='80' fill={WHITE} style={{fontFamily: FONT, fontSize: 34, fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
        {kg}
      </text>
      <text x={18 + kg.length * 19 + 6} y='80' fill={withOpacity(WHITE, 0.82)} style={{fontFamily: FONT, fontSize: 15, fontWeight: 600}}>
        kg
      </text>

      <text x='18' y='102' fill={withOpacity(WHITE, 0.82)} style={{fontFamily: FONT, fontSize: 11, fontVariantNumeric: 'tabular-nums'}}>
        {fmtDims(record.dims)}
      </text>
      <line x1='18' y1='112' x2={W - 18} y2='112' stroke={PRIMARY_BLUE} strokeWidth='1' strokeDasharray='3 4' opacity='0.55' />

      <text x='18' y='128' fill={withOpacity(WHITE, 0.72)} style={{fontFamily: FONT, fontSize: 10}}>
        No photograph on file
      </text>
      <text x={W - 18} y='128' textAnchor='end' fill={withOpacity(WHITE, 0.72)} style={{fontFamily: FONT, fontSize: 10}}>
        {record.courier}
      </text>
    </svg>
  )
}
